import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Gemeinde from "../DTOs/Gemeinde";
import Modal from "react-bootstrap/esm/Modal";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";

/** What the deleteGemeinde dry run reports (functions/src/gemeindeDeletion.ts). */
interface DeletionPlan {
    name: string;
    subcollections: Record<string, number>;
    scoped: Record<string, number>;
    users: { uid: string; email: string }[];
}

interface Props {
    gemeinde: Gemeinde | null;
    onClose: () => void;
    onDeleted: () => void;
}

/**
 * Confirmation for deleting a Gemeinde (GOD-34): shows what would be deleted
 * and only allows the deletion once the Gemeinde's name has been typed.
 */
export default function DeleteGemeindeDialog({ gemeinde, onClose, onDeleted }: Props) {
    const { t } = useTranslation();
    const [plan, setPlan] = useState<DeletionPlan | null>(null);
    const [typedName, setTypedName] = useState("");
    const [error, setError] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        setPlan(null);
        setTypedName("");
        setError("");
        if (!gemeinde) return;
        const deleteGemeinde = httpsCallable<object, { plan: DeletionPlan }>(functions, "deleteGemeinde");
        deleteGemeinde({ gemeindeId: gemeinde.id, dryRun: true })
            .then((result) => setPlan(result.data.plan))
            .catch((err) => setError(err?.message ?? t("errors.anErrorOccurred")));
    }, [gemeinde, t]);

    const confirmed = !!plan && plan.name.trim() !== "" && typedName.trim() === plan.name.trim();
    const total = (counts: Record<string, number>) => Object.values(counts).reduce((sum, n) => sum + n, 0);

    async function handleDelete() {
        if (!gemeinde || !confirmed) return;
        setDeleting(true);
        setError("");
        try {
            const deleteGemeinde = httpsCallable(functions, "deleteGemeinde");
            await deleteGemeinde({ gemeindeId: gemeinde.id, confirmName: typedName });
            onDeleted();
        } catch (err: any) {
            setError(err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setDeleting(false);
        }
    }

    return (
        <Modal show={!!gemeinde} onHide={deleting ? undefined : onClose} centered>
            <Modal.Header closeButton={!deleting}>
                <Modal.Title>{t("superadmin.deleteGemeinde.title", { name: gemeinde?.name ?? "" })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {!plan && !error && <Spinner animation="border" size="sm" />}
                {plan && (
                    <>
                        <p>{t("superadmin.deleteGemeinde.intro")}</p>
                        <ul data-testid="delete-gemeinde-plan">
                            {Object.entries(plan.subcollections).map(([name, count]) => (
                                <li key={name}>
                                    <code>{name}</code>: {count}
                                </li>
                            ))}
                            <li>
                                {t("superadmin.deleteGemeinde.scoped", { count: total(plan.scoped) })}
                            </li>
                            <li>
                                {t("superadmin.deleteGemeinde.users", { count: plan.users.length })}
                                {plan.users.length > 0 && ` (${plan.users.map((u) => u.email || u.uid).join(", ")})`}
                            </li>
                        </ul>
                        <Alert variant="warning" className="small">
                            {t("superadmin.deleteGemeinde.irreversible")}
                        </Alert>
                        <Form.Group controlId="delete-gemeinde-name">
                            <Form.Label>{t("superadmin.deleteGemeinde.typeName", { name: plan.name })}</Form.Label>
                            <Form.Control
                                value={typedName}
                                onChange={(e) => setTypedName(e.target.value)}
                                autoComplete="off"
                                disabled={deleting}
                            />
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={deleting}>
                    {t("common.cancel")}
                </Button>
                <Button variant="danger" onClick={handleDelete} disabled={!confirmed || deleting}>
                    {deleting ? <Spinner animation="border" size="sm" /> : t("superadmin.deleteGemeinde.confirm")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
